import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiDelete, apiFetch, apiPatch, apiPost } from "@/lib/api";
import { projectKeys, projectTeamKeys } from "@/lib/query";

export interface ProjectTeamMember {
  id: string;
  projectId: string;
  contactId: string;
  roleId: string;
  onSite: boolean;
  createdAt: string;
  role: { id: string; key: string; label: string; sortOrder: number };
  contact: {
    id: string;
    firstName: string;
    lastName: string;
    title: string | null;
    email: string | null;
    phone: string | null;
  };
}

export function useProjectTeam(projectId: string, enabled = true) {
  return useQuery({
    queryKey: projectTeamKeys.project(projectId),
    queryFn: () => apiFetch<ProjectTeamMember[]>(`/projects/${projectId}/team`),
    enabled: enabled && !!projectId,
  });
}

export function useAddProjectTeamMember(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { contactId: string; roleId: string }) =>
      apiPost<{ data: ProjectTeamMember }>(`/projects/${projectId}/team`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: projectTeamKeys.project(projectId) });
      qc.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
      qc.invalidateQueries({ queryKey: projectKeys.all });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useUpdateProjectTeamMember(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, patch }: { memberId: string; patch: { onSite?: boolean } }) =>
      apiPatch<{ data: ProjectTeamMember }>(`/projects/${projectId}/team/${memberId}`, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: projectTeamKeys.project(projectId) });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useRemoveProjectTeamMember(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) => apiDelete(`/projects/${projectId}/team/${memberId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: projectTeamKeys.project(projectId) });
      qc.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
      qc.invalidateQueries({ queryKey: projectKeys.all });
      toast.success("Team member removed");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
